import { on, Events } from '../../../../engine/core/events.js';
import { isMobileShellVisible } from '../../../../shared/utils/device-detect.js';
import { getTaskFilter, setTaskFilter, taskFilterMarkup, bindTaskFilter, TASK_FILTER_EVENT } from '../../ui/note-task-filter.js';
/** Find across every doc note in the open map and its loaded linked documents. */
import * as Store from '../../store/index.js';
import { findTextMatches, highlightText, searchSnippet } from '../../../../engine/core/text-search.js';
import { escapeHtml, escapeAttr } from '../../../../engine/core/validation.js';
import { registerSearchNavigation } from '../../ui/search-navigation.js';
import { textFromHtml, highlightSearchContent, clearSearchHighlights, revealSearchRange } from '../../ui/search-content.js';
import { renderMarkdown } from './markdown.js';
import { t, formatDate } from '../../../../shared/i18n/index.js';

/** The search bar of the notes panel and Notes view (texts in the current language). */
export const notesSearchMarkup = () => `<div class="notes-search" role="search" aria-label="${t('views.notesSearch.label')}">
  <div class="content-search-field">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="m16 16 4.5 4.5"/></svg>
    <input type="search" class="notes-search-input" placeholder="${t('views.notesSearch.placeholder')}" aria-label="${t('views.notesSearch.inputLabel')}" autocomplete="off" />
    <button type="button" class="search-icon-button" data-search-clear aria-label="${t('views.notesSearch.clearLabel')}" title="${t('views.notesSearch.clear')}" hidden>×</button>
  </div>
  <div class="note-task-filter-host"></div>
  <div class="search-navigation" hidden>
    <span data-search-status role="status" aria-live="polite"></span>
    <button type="button" class="search-icon-button" data-search-prev aria-label="${t('views.notesSearch.prevLabel')}" title="${t('views.notesSearch.prev')}">‹</button>
    <button type="button" class="search-icon-button" data-search-next aria-label="${t('views.notesSearch.nextLabel')}" title="${t('views.notesSearch.next')}">›</button>
  </div>
</div>`;

export function readSearchableNote(note, callbacks, docId = null) {
  // Once collaborative notes are enabled, the fragment is authoritative even
  // when empty. Never search a stale legacy copy of deleted text.
  if (callbacks.isCollabDocNotesEnabled?.(docId)) {
    return textFromHtml(callbacks.serializeDocNoteHtml?.(note.id, docId) || '');
  }
  return textFromHtml(renderMarkdown(String(note.content || ''), escapeHtml));
}

/** Shared desktop/mobile result builder; each note and match retains its owner. */
export function collectNoteSearch(callbacks, { query = '', task = null, readBody, mainOnly = false } = {}) {
  query = query.trim();
  const groups = [], matches = [];
  if (!query && !task) return { groups, matches };
  const relations = new Map((task ? callbacks.getRelatedDocNotes?.(task) || [] : [])
    .map(ref => [JSON.stringify([ref.docId, ref.noteId]), ref.reason]));
  let docs = callbacks.listDocsWithNotes?.() || [];
  if (!docs.length) docs = [{ isMain: true, title: t('views.notesSearch.currentMap') }];
  for (const doc of docs) {
    if (mainOnly && !doc.isMain) continue;
    const docId = doc.isMain ? null : doc.docId;
    for (const note of callbacks.getDocNotes?.(docId) || []) {
      const key = JSON.stringify([docId, note.id]);
      const title = note.title || formatDate(new Date(note.created), { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' });
      let reason = relations.get(key);
      if (task && !reason && !task.includeTitle) continue;
      const body = readBody ? readBody(note, docId) : readSearchableNote(note, callbacks, docId);
      if (task && !reason && task.includeTitle && task.title.trim() &&
          (findTextMatches(title, task.title).length || findTextMatches(body, task.title).length)) reason = t('views.noteReason.titleMentioned');
      if (task && !reason) continue;
      const titleMatches = findTextMatches(title, query), bodyMatches = findTextMatches(body, query);
      if (query && !titleMatches.length && !bodyMatches.length) continue;
      groups.push({ note, docId, docTitle: doc.title || t('views.notesSearch.currentMap'), title, body, reason, key,
        count: titleMatches.length + bodyMatches.length, titleMatches, bodyMatches });
    }
  }
  groups.sort((a,b) => new Date(b.note.created) - new Date(a.note.created));
  for (const group of groups) {
    group.first = matches.length;
    for (const [field, found] of [['title', group.titleMatches], ['body', group.bodyMatches]]) {
      found.forEach((match, occurrence) => matches.push({ group, noteId: group.note.id, docId: group.docId,
        field, occurrence, ...match, key: JSON.stringify([group.docId, group.note.id, field, match.start]) }));
    }
    if (!query) matches.push({ group, noteId: group.note.id, docId: group.docId, field: 'note',
      key: JSON.stringify([group.docId, group.note.id, 'note']) });
  }
  return { groups, matches };
}

export function createNotesSearch(ctx, host, {onRenderList, onSelectResult}, saved = null) {
  const bar = host.querySelector('.notes-search');
  const input = bar.querySelector('input');
  const status = bar.querySelector('[data-search-status]');
  const clear = bar.querySelector('[data-search-clear]');
  const previous = bar.querySelector('[data-search-prev]');
  const next = bar.querySelector('[data-search-next]');
  const highlightName = host.id === 'notes-overlay' ? 'orbital-note-search' : 'orbital-full-note-search';
  let scope = Store.get('currentDocId');
  if (saved?.scope !== scope) saved = null;
  if (saved) input.value = saved.query;
  let restoreKey = saved?.matchKey;
  let groups = [], matches = [], current = -1, timer = null, navigating = false;
  let query = '';
  const events = new AbortController();
  const isVisible = () => host.isConnected && !!host.getClientRects().length &&
    !isMobileShellVisible() &&
    (host.id !== 'notes-overlay' || host.classList.contains('active'));
  const navigation = registerSearchNavigation({
    available: isVisible,
    contains: target => host.contains(target), hasQuery: () => !!input.value.trim() || !!getTaskFilter(ctx.callbacks),
    focus: () => { input.focus({preventScroll:true}); input.select(); }, next: step,
  });

  function rebuild() {
    if (scope !== Store.get('currentDocId')) {
      scope = Store.get('currentDocId'); input.value = ''; query = ''; current = -1;
      clearSearchHighlights(highlightName);
    }
    const term = input.value.trim();
    const old = restoreKey || (term === query ? matches[current]?.key : null);
    restoreKey = null;
    query = term; groups = []; matches = [];
    const cb = ctx.callbacks;
    const task = getTaskFilter(cb);
    ({ groups, matches } = collectNoteSearch(cb, { query, task, readBody: (note, docId) => {
      const activeEditor = ctx.editorActive && ctx.tiptapEditor && !ctx.tiptapEditor.isDestroyed &&
        ctx.activeNotesDocId === docId && Store.get('currentNoteId') === note.id;
      return activeEditor ? textFromHtml(ctx.tiptapEditor.getHTML()) : readSearchableNote(note, cb, docId);
    }}));
    bar.querySelector('.note-task-filter-host').innerHTML = taskFilterMarkup(task, groups.length);
    input.placeholder = task ? t('views.notesSearch.relatedPlaceholder') : t('views.notesSearch.placeholder');
    const tabs = host.querySelector('.notes-doc-tabs');
    if (tabs) tabs.hidden = !!task || !!query;
    current = old ? matches.findIndex(match => match.key === old) : -1;
    if (current < 0 && task && !query) current = matches.findIndex(match => match.docId === ctx.activeNotesDocId && match.noteId === Store.get('currentNoteId'));
    status.textContent = query || task ? (matches.length ? `${current + 1 || '–'} / ${matches.length}` : t('views.search.noMatches')) : '';
    status.title = t('views.notesSearch.statusTitle', { count: groups.length });
    bar.querySelector('.search-navigation').hidden = !query && !task;
    previous.disabled = next.disabled = !matches.length;
    clear.hidden = !input.value;
  }

  function renderResults() {
    rebuild();
    const task = getTaskFilter(ctx.callbacks);
    if (!query && !task) return false;
    const restoreFocus = ctx.listEl.contains(document.activeElement) && document.activeElement.matches('[data-search-note]');
    ctx.listEl.setAttribute('role', 'region');
    ctx.listEl.setAttribute('aria-label', t('views.notesSearch.resultsLabel'));
    const selected = matches[current]?.group.key;
    ctx.listEl.innerHTML = groups.length ? groups.map(group => `<button type="button" class="notes-search-result${group.key === selected ? ' current' : ''}" data-search-note="${escapeAttr(group.key)}" aria-current="${group.key === selected}">
      <span class="notes-search-result-heading"><span>${highlightText(group.title,query, group.key === selected && matches[current]?.field === 'title' ? matches[current].occurrence : -1)}</span>${query ? `<small title="${t('views.notesSearch.matches', { count: group.count })}">${group.count}</small>` : ''}</span>
      <span class="notes-search-preview">${highlightText(searchSnippet(group.body,query || (task?.includeTitle ? task.title : '')),query)}</span>
      <span class="notes-search-document">${escapeHtml([formatDate(new Date(group.note.created), { year: 'numeric', month: 'numeric', day: 'numeric' }), group.reason, group.docTitle].filter(Boolean).join(' · '))}</span>
    </button>`).join('') : `<div class="notes-list-empty">${task ? (query ? t('views.notesSearch.noRelatedMatch') : t('views.notesSearch.noLinked')) : t('views.notesSearch.empty')}</div>`;
    ctx.listEl.querySelectorAll('[data-search-note]').forEach(button => {
      const select = (offset = 0) => {
        const index = groups.findIndex(group => group.key === button.dataset.searchNote);
        const group = groups[Math.max(0, Math.min(groups.length - 1, index + offset))];
        if (group) go(group.first);
        ctx.listEl.querySelector('.notes-search-result.current')?.focus({ preventScroll: true });
      };
      button.addEventListener('click', () => select());
      button.addEventListener('keydown', event => {
        if (event.isComposing || event.ctrlKey || event.metaKey || event.altKey) return;
        if (!['ArrowUp', 'ArrowDown', 'Enter', ' '].includes(event.key)) return;
        event.preventDefault(); event.stopPropagation();
        select(event.key === 'ArrowUp' ? -1 : event.key === 'ArrowDown' ? 1 : 0);
      });
    });
    if (restoreFocus) ctx.listEl.querySelector('.notes-search-result.current')?.focus({ preventScroll: true });
    return true;
  }

  function highlight(reveal = false) {
    const match = matches[current];
    const selected = match && match.group.docId === ctx.activeNotesDocId && match.group.note.id === Store.get('currentNoteId');
    const range = highlightSearchContent(ctx.editorMount, query,
      selected && match.field === 'body' ? match.occurrence : -1, highlightName);
    if (reveal && selected) {
      const row = ctx.listEl.querySelector(`[data-search-note="${CSS.escape(match.group.key)}"]`);
      if (row) {
        const bounds = ctx.listEl.getBoundingClientRect(), box = row.getBoundingClientRect();
        if (box.top < bounds.top || box.bottom > bounds.bottom) ctx.listEl.scrollTop += box.top - bounds.top;
      }
      revealSearchRange(range, ctx.editorMount);
    }
  }

  function go(index) {
    if (!matches.length || navigating) return;
    const previousKey = matches[current]?.key;
    current = (index + matches.length) % matches.length;
    const match = matches[current];
    navigation.activate();
    navigating = true;
    try {
      if (onSelectResult(match.group) === false) {
        current = matches.findIndex(match => match.key === previousKey);
        onRenderList();
        highlight();
        return;
      }
    } finally { navigating = false; }
    onRenderList();
    highlight(true);
  }
  function step(direction) {
    clearTimeout(timer); timer = null; rebuild();
    go(current < 0 ? (direction > 0 ? 0 : matches.length - 1) : current + direction);
  }
  function reset() {
    clearTimeout(timer); timer = null; input.value = ''; query = ''; current = -1;
    clearSearchHighlights(highlightName); onRenderList();
  }
  bar.addEventListener('focusin', () => navigation.activate(), {signal:events.signal});
  input.addEventListener('input', () => {
    clearTimeout(timer);
    timer = setTimeout(() => { timer = null; onRenderList(); if (matches.length) go(0); else highlight(); },120);
  }, {signal:events.signal});
  input.addEventListener('keydown', event => {
    if (event.isComposing) return;
    if (['Enter','ArrowDown','ArrowUp','Escape'].includes(event.key)) {
      event.preventDefault(); event.stopPropagation();
      if (event.key === 'Escape') {
        if (input.value) reset();
        else if (getTaskFilter(ctx.callbacks)) setTaskFilter(null);
      } else step(event.key === 'ArrowUp' || event.shiftKey ? -1 : 1);
    }
  }, {signal:events.signal});
  previous.addEventListener('click', () => step(-1), {signal:events.signal});
  next.addEventListener('click', () => step(1), {signal:events.signal});
  clear.addEventListener('click', () => { reset(); input.focus({preventScroll:true}); }, {signal:events.signal});
  bindTaskFilter(bar, ctx.callbacks, { signal: events.signal });
  document.addEventListener(TASK_FILTER_EVENT, event => {
    clearTimeout(timer); timer = null;
    if (event.detail?.resetQuery) { input.value = ''; current = -1; }
    onRenderList();
    if (getTaskFilter(ctx.callbacks) && isVisible()) {
      navigation.activate();
      go(current < 0 ? 0 : current);
    }
  }, { signal: events.signal });
  let refreshFrame = null;
  const unsubscribe = [Events.EXECUTION_CHANGED, Events.ROUTINES_CHANGED, Events.DOC_NOTES_CHANGED].map(type => on(type, () => {
    if (!isVisible() || (!getTaskFilter(ctx.callbacks) && !input.value.trim()) || refreshFrame !== null) return;
    refreshFrame = requestAnimationFrame(() => { refreshFrame = null; onRenderList(); highlight(); });
  }));
  if (saved) queueMicrotask(() => {
    if (!input.isConnected || Store.get('currentDocId') !== scope) return;
    if (saved.pending) { onRenderList(); if (matches.length) go(0); else highlight(); }
    if (saved.focused) {
      input.focus({preventScroll:true}); input.setSelectionRange(saved.start, saved.end);
    }
  });
  return {renderResults, highlight, reset,
    capture: () => ({scope, query:input.value, matchKey:matches[current]?.key,
      focused:document.activeElement === input, start:input.selectionStart, end:input.selectionEnd, pending:timer !== null}),
    dispose: () => { cancelAnimationFrame(refreshFrame); unsubscribe.forEach(stop => stop()); clearTimeout(timer); events.abort(); navigation.dispose(); clearSearchHighlights(highlightName); groups = []; matches = []; }};
}
